import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, User, LogOut, Home, Users, MapPin, Activity, Zap, MessageSquare } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { usePWA } from "../hooks/usePWA";
import { useFetch } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import MobileNavigation from "./MobileNavigation";

const Navigation = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { user, logout, loading } = useAuth();
  const { isMobilePWA, isLoading: pwaLoading } = usePWA();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Fetch club data for runclub users
  const { data: clubData } = useFetch(
    () => apiService.clubs.getById(user?.club_id),
    [user?.club_id],
    { enabled: !!user?.club_id && user?.user_type === 'runclub' }
  );

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu on escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Handle scroll to hide/show navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY <= 100) {
        // At the top of the page - always show navbar
        setIsVisible(true);
      } else {
        // Not at top - hide navbar
        setIsVisible(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  }, [logout, navigate]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  const navItems = [
    { to: "/", label: "Home", icon: Home },
    { to: "/run", label: "Run", icon: Zap, special: true },
    ...(user ? [{ 
      to: user.user_type === "runclub" ? "/myevents" : "/myruns", 
      label: user.user_type === "runclub" ? "My Runs" : "My Runs", 
      icon: Activity 
    }] : []),
    { to: "/runclubs", label: "Run Clubs", icon: Users },
    ...(user ? [{ 
      to: "/messages", 
      label: "Messages", 
      icon: MessageSquare 
    }] : []),
  ];

  // Show loading state while PWA detection is in progress
  if (pwaLoading) {
    return null; // Let the app render normally while PWA detection loads
  }

  // Mobile PWA navigation disabled - always show top navigation
  // if (isMobilePWA) {
  //   return <MobileNavigation />;
  // }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-transparent transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 sm:h-28 md:h-32">
            <Link to="/" className="flex items-center -mt-2 sm:-mt-3 md:-mt-4">
              <div className="w-36 h-36 sm:w-48 sm:h-48 md:w-56 md:h-56 flex items-center justify-center">
                {logoError ? (
                  <span className="text-white font-bold text-lg sm:text-xl md:text-2xl">RUNCARDS</span>
                ) : (
                  <img 
                    src="/RunCards_full_white_text_transparent.png" 
                    alt="Runcards Logo" 
                    className="w-full h-full object-contain"
                    onError={() => setLogoError(true)}
                    loading="eager"
                  />
                )}
              </div>
            </Link>
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-white/10 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-transparent transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        {/* Logo - Fixed position at top left */}
        <Link 
          to="/" 
          className={`fixed -top-16 left-0 z-50 flex items-start transition-transform duration-300 ${
            isVisible ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="w-36 h-36 sm:w-48 sm:h-48 md:w-56 md:h-56 flex items-center justify-center">
            {logoError ? (
              <span className="text-white font-bold text-lg sm:text-xl md:text-2xl">RUNCARDS</span>
            ) : (
              <img 
                src="/RunCards_full_white_text_transparent.png" 
                alt="Runcards Logo" 
                className="w-full h-full object-contain"
                onError={() => setLogoError(true)}
                loading="eager"
              />
            )}
          </div>
        </Link>
      </nav>

      {/* Hamburger Menu Button */}
      <button
        onClick={toggleMenu}
        className={`fixed top-4 right-4 z-50 flex items-center px-4 py-3 rounded-xl text-sm font-semibold text-white/70 hover:text-white hover:bg-white/8 border border-white/10 hover:border-white/20 hover:backdrop-blur-sm hover:shadow-lg hover:shadow-white/5 transition-all duration-300 focus:outline-none focus:ring-0 focus:border-0 ${
          isVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        style={{fontFamily: 'Montserrat, sans-serif'}}
      >
        <Menu className="w-5 h-5 mr-2 transition-transform duration-300 hover:scale-110" />
        Menu
      </button>

      {/* Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Menu Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-black/20 backdrop-blur-2xl border-l border-white/30 z-50 transform transition-transform duration-300 ease-in-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Menu Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center">
            <span className="text-white font-semibold" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Menu
            </span>
          </div>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors focus:outline-none focus:ring-0 focus:border-0"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex flex-col p-6 space-y-2">
          {navItems.map(({ to, label, icon: Icon, special }, index) => (
            <Link
              key={to}
              to={to}
              className={`flex items-center px-4 py-4 rounded-xl text-base font-medium transition-all duration-300 ${
                special 
                  ? location.pathname === to
                    ? 'text-white bg-white/15 backdrop-blur-sm border border-white/30 shadow-lg shadow-white/10'
                    : 'text-white/70 hover:text-white hover:bg-white/8 border border-white/10 hover:border-white/20 hover:backdrop-blur-sm hover:shadow-lg hover:shadow-white/5'
                  : location.pathname === to
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
              }`}
              style={{fontFamily: 'Montserrat, sans-serif'}}
              onClick={() => setIsMenuOpen(false)}
            >
              <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${special ? 'transition-transform duration-300 hover:scale-110' : ''}`} />
              {label}
            </Link>
          ))}
        </div>

        {/* User Section */}
        <div className="p-6 border-t border-white/10">
          {user ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 rounded-xl bg-white/5">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center overflow-hidden">
                  {user.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    {user.user_type === "runclub" ? (clubData?.name || "Profile") : "Profile"}
                  </p>
                  <p className="text-white/60 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    {user.user_type === "runclub" ? "Run Club" : "Runner"}
                  </p>
                </div>
              </div>
              
              <Link 
                to="/profile" 
                className="flex items-center px-4 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                <User className="w-5 h-5 mr-3" />
                <span style={{fontFamily: 'Montserrat, sans-serif'}}>Profile</span>
              </Link>
              
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200 focus:outline-none focus:ring-0 focus:border-0"
              >
                <LogOut className="w-5 h-5 mr-3" />
                <span style={{fontFamily: 'Montserrat, sans-serif'}}>Logout</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                <Button 
                  variant="ghost" 
                  className="w-full text-white/80 hover:text-white hover:bg-white/10 border border-white/20 hover:border-white/30 text-base py-4 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-white/10"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  Login
                </Button>
              </Link>
              <Link to="/auth?type=signup" onClick={() => setIsMenuOpen(false)}>
                <Button 
                  className="w-full bg-gradient-to-r from-white to-gray-100 text-black hover:from-gray-100 hover:to-white hover:scale-105 text-base py-4 font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  Sign Up
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <p className="text-white/40 text-sm text-center" style={{fontFamily: 'Montserrat, sans-serif'}}>
            © 2024 Runcards
          </p>
        </div>
      </div>
    </>
  );
};

export default Navigation;